import { LogoutService } from "./Logout.service";
import { SpinnerService } from "./spinner.service";
import { Inject, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Observable, Subscriber } from "rxjs";
import { NetworkErrorDisconnectedComponent } from "@app/components/networkError/network.error.disconnected.component";
import { take } from "rxjs/operators";
import { NetworkErrorTimeoutComponent } from "@app/components/networkError/network.error.timeout.component";
import { NetworkErrorInternalComponent } from "@app/components/networkError/network.error.internal.component";
import { OneButtonDialogComponent } from "@app/components/sharedDialogs/oneButtonDialog.component";

const RESOLVE_RETRY = true

@Injectable({ providedIn: "root" })
export class ErrorHandlerService {
    timeoutTimer: any;
    timeoutSeconds: number = 125;  // backend is set to 2 min so this is 5 sec longer

    constructor(@Inject(MatDialog) private dialog: MatDialog,
                @Inject(LogoutService) private logoutService: LogoutService,
                @Inject(SpinnerService) private spinnerService: SpinnerService){}

    /** @returns Promise which resolves to a boolean of whether to retry or not  */
    handleNetworkError(error: any, canClose: boolean, errorMsg?: string): Observable<boolean|string> {
        if (!window.__karma__) console.error(new Error('handleNetworkError called').stack)

        this.spinnerService.closeSpinner();

        /** resolve true to retry request */
        return new Observable<boolean|string>((observer) => {
            if (error && error.status) {
                if (error.status === -1 || error.status === 408 || error.status === 504) {
                    //No connection to server, or request timed out
                    this.timeoutDialog(observer, canClose);

                } else if (error.status === 500) {
                    //leaving this as a separate else, this way its easier to do something different with 500 errors in the future
                    this.internalErrorDialog(observer, canClose, true, errorMsg);

                } else {
                    this.internalErrorDialog(observer, canClose, true, errorMsg);
                    // defer.resolve("Error is not a network error with status of -1, 408, or 504");
                }

            } else if (error && error.disconnected) {
                this.disconnectedDialog(observer)

            } else {
                if (/valid(ate)?\s*\w*\s*card/i.test(error) || error === "An error occurred when attempting to validate the card." || error === "Invalid card number.") {
                    this.internalErrorDialog(observer, canClose, true, errorMsg);

                } else if (typeof error === 'string') {
                    this.internalErrorDialog(observer, canClose, true, error);

                } else {
                    if (!window.__karma__) console.error("handleNetworkError: error does not have status", {
                        error,
                        canClose
                    })
                    this.internalErrorDialog(observer, canClose, true, errorMsg);
                }
            }
        });
    }

    disconnected() {
        this.disconnectedDialog(null)
    }

    reconnected() {
        this.spinnerService.closeSpinner();
        window.clearTimeout(this.timeoutTimer);
    }

    disconnectedDialog(observer: Subscriber<boolean|string>) {
        this.spinnerService.closeSpinner();

        const dialogRef = this.dialog.open(NetworkErrorDisconnectedComponent, {
            closeOnNavigation: false,
            disableClose: true,
        })

        dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
            if (observer) {
                observer.next("No connection error")
            }
        })
    }

    timeoutDialog(observer: Subscriber<boolean>, canClose: boolean) {
        const dialogRef = this.dialog.open(NetworkErrorTimeoutComponent, {
            closeOnNavigation: false,
            disableClose: !canClose,
        })

        dialogRef.afterClosed().subscribe((returnedValue) => {
            if (returnedValue === 'Logout') {
                this.logoutService.logout();
                observer.next(false);
            }
            else if (returnedValue === 'Retry') {
                observer.next(RESOLVE_RETRY);
            }
            else {
                observer.next(false);
            }
        });
    }

    internalErrorDialog(observer: Subscriber<boolean>, canClose: boolean, removeRetry?: boolean, message?: string) {
        const dialogRef = this.dialog.open(NetworkErrorInternalComponent, {
            closeOnNavigation: false,
            disableClose: !canClose,
            data:  { message: message, removeRetry: removeRetry },
        })

        dialogRef.afterClosed().pipe(take(1)).subscribe((returnedValue) => {
            if (returnedValue === 'Retry') {
                observer.next(RESOLVE_RETRY);
            } else {
                observer.next(false);
            }
        })
    }

    showPaymentErrorDialog(header: string, message: string, message2?: string, buttonText: string = "OK"): Observable<void> {
        const dialogRef = this.dialog.open(OneButtonDialogComponent, {
            data: { header, message, message2, buttonText },
        });
        return dialogRef.afterClosed().pipe(take(1));
    }
}

